import { useState, type FormEvent } from 'react'
import { Eye, EyeOff, LockKeyhole } from 'lucide-react'
import { login } from '../infrastructure/api'

export default function LoginPage({
  onSignedIn,
  sessionError,
}: {
  onSignedIn: (role: string) => Promise<void>
  sessionError?: string
}) {
  const [userId, setUserId] = useState('')
  const [password, setPassword] = useState('')
  const [reveal, setReveal] = useState(false)
  const [errors, setErrors] = useState<{ userId?: string; password?: string; general?: string }>({})
  const [submitting, setSubmitting] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const next = {
      userId: userId.trim() ? '' : 'Заполните поле',
      password: password ? '' : 'Заполните поле',
    }
    if (next.userId || next.password) {
      setErrors(next)
      return
    }
    setErrors({})
    setSubmitting(true)
    try {
      const user = await login(userId.trim(), password)
      await onSignedIn(user.role)
      setPassword('')
    } catch (error) {
      setErrors({ general: error instanceof Error ? error.message : 'Не удалось войти' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="content login-page">
      <div className="login-card">
        <div className="login-mark">
          <LockKeyhole aria-hidden="true" />
        </div>
        <span className="eyebrow">Магистраль · Механизация</span>
        <h1>Вход в систему</h1>
        <p>Используйте свой рабочий логин и пароль.</p>
        {sessionError && (
          <p className="login-note" role="status">
            {sessionError}
          </p>
        )}
        <form onSubmit={submit} noValidate>
          <label htmlFor="login-user">Логин</label>
          <input
            id="login-user"
            name="username"
            autoComplete="username"
            value={userId}
            aria-invalid={Boolean(errors.userId)}
            aria-describedby={errors.userId ? 'login-user-error' : undefined}
            onChange={(event) => {
              setUserId(event.target.value)
              setErrors((old) => ({ ...old, userId: '', general: '' }))
            }}
          />
          {errors.userId && (
            <span id="login-user-error" className="field-error">
              {errors.userId}
            </span>
          )}
          <label htmlFor="login-password">Пароль</label>
          <div className="password-field">
            <input
              id="login-password"
              name="password"
              autoComplete="current-password"
              type={reveal ? 'text' : 'password'}
              value={password}
              aria-invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? 'login-password-error' : undefined}
              onChange={(event) => {
                setPassword(event.target.value)
                setErrors((old) => ({ ...old, password: '', general: '' }))
              }}
            />
            <button
              type="button"
              className="icon-button"
              onClick={() => setReveal((value) => !value)}
              aria-label={reveal ? 'Скрыть пароль' : 'Показать пароль'}
            >
              {reveal ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </div>
          {errors.password && (
            <span id="login-password-error" className="field-error">
              {errors.password}
            </span>
          )}
          {errors.general && (
            <p className="field-error" role="alert">
              {errors.general}
            </p>
          )}
          <button className="button primary block" disabled={submitting} type="submit">
            {submitting ? 'Входим…' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  )
}
